import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getLoaStats,
  getLoaSenderStatus,
  batchSendLoaRequests,
  enableLoaSender,
  disableLoaSender,
  runLoaSenderNow,
  LoaStats,
  LoaSenderStatus,
  BulkSendResult,
} from '../api/loaSender';
import { PageHeader, Card, Button, StatusPill } from './ui';
import './LOASenderManagement.css';

const LOASenderManagement: React.FC = () => {
  const { isAdmin } = useAuth();

  const [stats, setStats] = useState<LoaStats | null>(null);
  const [senderStatus, setSenderStatus] = useState<LoaSenderStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<BulkSendResult | null>(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmAll, setConfirmAll] = useState(false);
  const [toggling, setToggling] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setError('');
      const [statsData, statusData] = await Promise.all([
        getLoaStats(),
        getLoaSenderStatus(),
      ]);
      setStats(statsData);
      setSenderStatus(statusData);
    } catch (err: any) {
      console.error('Failed to load LOA sender data:', err);
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleBatchSend = async (limit?: number) => {
    if (limit === undefined && !confirmAll) {
      setConfirmAll(true);
      return;
    }

    setSending(true);
    setSendResult(null);
    setError('');
    setSuccessMessage('');
    setConfirmAll(false);

    try {
      const result = await batchSendLoaRequests(limit);
      setSendResult(result);
      setSuccessMessage(`Batch complete: ${result.sent} sent, ${result.failed} failed, ${result.skipped} skipped`);
      loadData();
    } catch (err: any) {
      console.error('Batch send failed:', err);
      setError(err.response?.data?.error || 'Failed to send LOA requests');
    } finally {
      setSending(false);
    }
  };

  const handleToggle = async () => {
    setToggling(true);
    setError('');
    setSuccessMessage('');

    try {
      if (senderStatus?.isEnabled) {
        await disableLoaSender();
        setSuccessMessage('LOA sender disabled');
      } else {
        await enableLoaSender();
        setSuccessMessage('LOA sender enabled (9 AM & 2 PM daily)');
      }
      const statusData = await getLoaSenderStatus();
      setSenderStatus(statusData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to toggle LOA sender');
    } finally {
      setToggling(false);
    }
  };

  const handleRunNow = async () => {
    setError('');
    setSuccessMessage('');
    try {
      await runLoaSenderNow();
      setSuccessMessage('LOA sender run triggered. Check status for results.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to trigger run');
    }
  };

  if (!isAdmin) {
    return <div className="status-message loa-forbidden">Admin access required.</div>;
  }

  if (loading) {
    return <div className="status-message">Loading LOA sender data...</div>;
  }

  return (
    <div className="loa-sender-management">
      <PageHeader title="LOA Sender Management" />

      {error && <div className="loa-alert loa-alert-error">{error}</div>}
      {successMessage && <div className="loa-alert loa-alert-success">{successMessage}</div>}

      {/* Stats Section */}
      {stats && (
        <Card className="loa-section" title="LOA Coverage Statistics">
          <div className="loa-stats-grid">
            <div className="loa-stat-card">
              <div className="stat-label">Total Active Clients</div>
              <div className="stat-value">{stats.totalActiveClients.toLocaleString()}</div>
            </div>
            <div className="loa-stat-card">
              <div className="stat-label">LOA Sent</div>
              <div>
                <span className="stat-value loa-text-primary">{stats.loaSent.toLocaleString()}</span>
                <span className="stat-subtitle loa-text-primary">{stats.sentPercentage}%</span>
              </div>
            </div>
            <div className="loa-stat-card">
              <div className="stat-label">LOA Signed</div>
              <div>
                <span className="stat-value loa-text-success">{stats.loaSigned.toLocaleString()}</span>
                <span className="stat-subtitle loa-text-success">{stats.signedPercentage}%</span>
              </div>
            </div>
            <div className="loa-stat-card">
              <div className="stat-label">Pending (Unsigned)</div>
              <div className="stat-value loa-text-warning">{stats.pending.toLocaleString()}</div>
            </div>
            <div className="loa-stat-card">
              <div className="stat-label">Never Sent</div>
              <div className="stat-value loa-text-danger">{stats.neverSent.toLocaleString()}</div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="loa-progress-bar">
            <div className="loa-progress-track">
              <div className="loa-progress-signed" style={{ width: `${stats.signedPercentage}%` }} />
              <div className="loa-progress-pending" style={{ width: `${Math.max(stats.sentPercentage - stats.signedPercentage, 0)}%` }} />
            </div>
            <div className="loa-progress-legend">
              <span><span className="loa-legend-dot loa-legend-dot--signed" /> Signed</span>
              <span><span className="loa-legend-dot loa-legend-dot--pending" /> Pending</span>
              <span><span className="loa-legend-dot loa-legend-dot--notsent" /> Not Sent</span>
            </div>
          </div>
        </Card>
      )}

      {/* Manual Batch Send */}
      <Card className="loa-section" title="Manual Batch Send">
        <p className="description">Send LOA questionnaire links to eligible clients who haven't been sent one yet.</p>

        <div className="loa-button-group">
          {[1, 100, 500, 1000].map(limit => (
            <Button
              key={limit}
              variant="primary"
              onClick={() => handleBatchSend(limit)}
              disabled={sending}
            >
              Send {limit}
            </Button>
          ))}

          {!confirmAll ? (
            <Button
              variant="danger"
              onClick={() => handleBatchSend(undefined)}
              disabled={sending}
            >
              Send All Outstanding
            </Button>
          ) : (
            <div className="loa-confirm-prompt">
              <span className="confirm-text">Are you sure?</span>
              <Button
                variant="danger"
                onClick={() => handleBatchSend(undefined)}
                disabled={sending}
              >
                Yes, Send All
              </Button>
              <Button
                variant="secondary"
                onClick={() => setConfirmAll(false)}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>

        {sending && (
          <div className="loa-sending-indicator">
            Sending LOA requests... This may take a while for large batches.
          </div>
        )}

        {sendResult && (
          <div className="loa-results">
            <h4>Batch Results</h4>
            <div className="loa-results-grid">
              <div>Sent: <strong className="loa-text-success">{sendResult.sent}</strong></div>
              <div>Failed: <strong className="loa-text-danger">{sendResult.failed}</strong></div>
              <div>Skipped: <strong className="loa-text-warning">{sendResult.skipped}</strong></div>
              <div>Total Eligible: <strong>{sendResult.total_eligible}</strong></div>
            </div>
            {sendResult.mailer_error_halted && (
              <div className="loa-halted-warning">
                Batch halted due to MailerSend API error.
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Auto-Processor Section */}
      <Card className="loa-section" title="Auto-Processor">
        <p className="description">When enabled, automatically sends LOA requests to new eligible clients at 9:00 AM and 2:00 PM GMT daily.</p>

        <div className="loa-button-group loa-button-group--gap">
          <Button
            variant={senderStatus?.isEnabled ? 'danger' : 'primary'}
            onClick={handleToggle}
            disabled={toggling}
          >
            {toggling ? 'Processing...' : senderStatus?.isEnabled ? 'Disable' : 'Enable'}
          </Button>

          <StatusPill
            status={senderStatus?.isEnabled ? 'running' : 'disabled'}
            label={senderStatus?.isEnabled ? 'ENABLED' : 'DISABLED'}
          />

          {senderStatus?.isEnabled && (
            <Button variant="secondary" onClick={handleRunNow}>
              Run Now
            </Button>
          )}
        </div>

        {/* Status Details */}
        {senderStatus && (senderStatus.nextRuns.length > 0 || senderStatus.lastRunTime) && (
          <div className="loa-status-details">
            {senderStatus.nextRuns.length > 0 && (
              <div><strong>Next runs:</strong> {senderStatus.nextRuns.map(r => new Date(r).toLocaleString()).join(', ')}</div>
            )}
            {senderStatus.lastRunTime && (
              <div>
                <strong>Last run:</strong> {new Date(senderStatus.lastRunTime).toLocaleString()}
                {senderStatus.lastTriggeredBy && ` (${senderStatus.lastTriggeredBy})`}
              </div>
            )}
            {senderStatus.lastRunResults && (
              <div>
                <strong>Last results:</strong> {senderStatus.lastRunResults.sent} sent,{' '}
                {senderStatus.lastRunResults.failed} failed,{' '}
                {senderStatus.lastRunResults.skipped} skipped
                {senderStatus.lastRunResults.mailer_error_halted && ' (halted by error)'}
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default LOASenderManagement;
